import "./styles.css";
import { AudioManager } from "./audio/AudioManager";
import { Game } from "./game/Game";
import { KeyboardInput } from "./input/KeyboardInput";
import { GamepadInput } from "./input/GamepadInput";
import { LookingGlassAdapter } from "./lookingglass/LookingGlassAdapter";
import { SceneRenderer } from "./rendering/SceneRenderer";
import { Diagnostics } from "./lookingglass/Diagnostics";

async function boot(): Promise<void> {
  const canvas = document.querySelector<HTMLCanvasElement>("#game");
  const status = document.querySelector<HTMLElement>("#status");
  if (!canvas || !status) throw new Error("Required UI elements are missing");

  status.textContent = "Looking Glass WebXRを初期化中…";
  try {
    await LookingGlassAdapter.initialize();
    status.textContent = "WebXR準備完了 — 機器の校正を確認中…";
  } catch (error) {
    console.error("Looking Glass initialization failed", error);
    status.textContent = "WebXR初期化失敗 — ページを再読み込みしてください";
  }

  const game = new Game();
  const scene = new SceneRenderer(canvas, game);
  const input = new KeyboardInput(game, () => !scene.isGameOverAnimating, () => !scene.diagnosticMode);
  const gamepad = new GamepadInput(game, () => !scene.isGameOverAnimating, () => !scene.diagnosticMode,
    message => { document.querySelector<HTMLElement>("#gamepad-status")!.textContent = message; },
    message => { document.querySelector<HTMLElement>("#gamepad-input")!.textContent = message; },
    (x, y, seconds) => scene.rotateView(x, y, seconds), () => scene.resetView());
  document.querySelector<HTMLButtonElement>("#reset-view")!.addEventListener("click", () => scene.resetView());
  const viewStatus = document.querySelector<HTMLElement>("#view-angle")!;
  const soundStatus = document.querySelector<HTMLElement>("#sound-status")!;
  const audio = new AudioManager(message => { soundStatus.textContent = message; });
  const muted = document.querySelector<HTMLInputElement>("#sound-muted")!;
  muted.addEventListener("change", () => audio.setMuted(muted.checked));
  document.querySelector<HTMLInputElement>("#music-enabled")!.addEventListener("change", event => audio.setMusicEnabled((event.target as HTMLInputElement).checked));
  document.querySelector<HTMLInputElement>("#sound-volume")!.addEventListener("input", event => {
    const value = (event.target as HTMLInputElement).value;
    document.querySelector<HTMLOutputElement>("#sound-volume-value")!.value = `${value}%`;
    audio.setVolume(Number(value) / 100);
  });
  const soundWindows = new WeakSet<Window>();
  const attachSoundKeys = (target: Window) => {
    if (soundWindows.has(target)) return;
    soundWindows.add(target);
    target.addEventListener("keydown", event => {
      if (event.code !== "KeyM" || event.repeat || (event.target as HTMLElement)?.closest("input, button, select, textarea")) return;
      event.preventDefault();
      muted.checked = !muted.checked;
      audio.setMuted(muted.checked);
    });
  };
  attachSoundKeys(window);
  LookingGlassAdapter.attach(scene.renderer, status, popup => {
    input.attachWindow(popup);
    gamepad.attachWindow(popup);
    audio.attachWindow(popup);
    attachSoundKeys(popup);
  });
  const diagnostics = new Diagnostics(scene.renderer, status);
  const mode = document.querySelector<HTMLButtonElement>("#test-mode")!;
  const setMode = (enabled: boolean) => {
    scene.setDiagnosticMode(enabled);
    mode.textContent = enabled ? "ゲームに戻る" : "立体視テストを開く";
    mode.setAttribute("aria-pressed", String(enabled));
    document.querySelector<HTMLElement>("#test-instructions")!.hidden = !enabled;
    document.querySelector<HTMLDetailsElement>("#connection-details")!.open = enabled;
    if (!enabled) (LookingGlassAdapter.config.lkgCanvas ?? canvas).focus();
  };
  mode.addEventListener("click", () => setMode(!scene.diagnosticMode));
  document.querySelector<HTMLInputElement>("#focus-only")!.addEventListener("change", event => {
    scene.setFocusOnly((event.target as HTMLInputElement).checked);
  });
  setMode(new URLSearchParams(location.search).get("mode") === "depth-test");

  let previous = performance.now();
  scene.renderer.setAnimationLoop((time) => {
    const delta = Math.min(100, time - previous);
    previous = time;
    const presenting = scene.renderer.xr.isPresenting;
    gamepad.update(time);
    if (!scene.diagnosticMode) {
      input.update(time);
      game.update(delta);
    }
    const events = game.drainEvents();
    audio.handle(events);
    gamepad.handle(events);
    audio.update(scene.diagnosticMode ? "paused" : game.phase, game.level);
    scene.handle(events);
    scene.update(delta / 1000);
    const view = scene.viewAngles;
    const viewText = `視点: ${Math.round(view.yaw)}° / 見下ろし ${Math.round(view.pitch)}°`;
    if (viewStatus.textContent !== viewText) viewStatus.textContent = viewText;
    if (presenting) {
      const output = LookingGlassAdapter.config.lkgCanvas;
      const label = canvas.getAttribute("aria-label")!;
      if (output && output.getAttribute("aria-label") !== label) output.setAttribute("aria-label", label);
    }
    scene.render();
    diagnostics.update(time);
  });
}

void boot();
